import { tourFunctions } from "../tour-functions";
const { getIndex } = tourFunctions.currentScene;
const getCurrentSceneIndex = getIndex;

/** How many scenes ahead/behind to warm previews for (fast next/prev clicks). */
const PREVIEW_RADIUS = 2;
/** Max parallel queue downloads. */
const PRELOAD_CONCURRENCY = 2;

/** Lower number = higher priority. */
const PRIORITY = {
  PREVIEW: 1,
  PANO: 2,
  HOTSPOT: 3,
  THUMB: 4,
};

function isAbortError(error) {
  return (
    error?.name === "AbortError" ||
    (typeof DOMException !== "undefined" &&
      error instanceof DOMException &&
      error.name === "AbortError")
  );
}

function resolveTourAssetUrl(url) {
  if (!url || typeof url !== "string") return null;
  return url.replace(/%SWFPATH%/g, ".");
}

const HOTSPOT_STYLE_ICON_DEFAULTS = {
  hotspot_info: "%SWFPATH%/images/info.svg",
  hotspot_info_custom: "%SWFPATH%/images/info.svg",
  // Directions always use an Iconify url on the hotspot; no default info icon.
  hotspot_gallery: "%SWFPATH%/images/gallery.svg",
  hotspot_video: "%SWFPATH%/images/video.svg",
  hotspot_social: "%SWFPATH%/images/social-instagram-reel.svg",
  hotspot_accessibility: "%SWFPATH%/images/accessibility/accessibility-2.svg",
  infosidebar: "%SWFPATH%/images/info.svg",
};

const TOUR_HOTSPOT_ICON_KEYS = {
  hotspot_info: "hotspotInfo",
  hotspot_info_custom: "hotspotInfo",
  hotspot_gallery: "hotspotGallery",
  hotspot_video: "hotspotVideo",
  hotspot_social: "hotspotSocial",
};

const LINKED_SCENE_HOTSPOT_STYLES = new Set([
  "skin_hotspotstyle",
  "skin_hotspotstyle_balloon",
]);

/** Shared across Preloader instances (Loader + Tour) to avoid duplicate fetches. */
const globalLoadedUrls = new Set();

/** Blocks background preloads until the loader finishes the start scene. */
const firstSceneGate = {
  complete: false,
  /** @type {Promise<void> | null} */
  promise: null,
  /** @type {(() => void) | null} */
  resolve: null,
};

function beginFirstScenePreload() {
  firstSceneGate.complete = false;
  firstSceneGate.promise = new Promise((resolve) => {
    firstSceneGate.resolve = resolve;
  });
}

function completeFirstScenePreload() {
  firstSceneGate.complete = true;
  firstSceneGate.resolve?.();
  firstSceneGate.resolve = null;
}

async function waitForFirstScenePreload() {
  if (firstSceneGate.complete) return;
  if (firstSceneGate.promise) await firstSceneGate.promise;
}

// Preloader.js
export class Preloader {
  constructor(tour, directions = ["u", "d", "f", "b", "l", "r"]) {
    this.tour = tour;
    this.directions = directions;
    this.scenes = tour.scenes?.filter((scene) => !scene.hidden);
    this.isStopped = false;
    this.loadedUrls = new Set();
    this.preloadedScenePanoIndices = new Set();
    this.preloadedScenePreviewIndices = new Set();

    /** @type {Array<{ url: string, priority: number, epoch: number }>} */
    this.queue = [];
    /** @type {Map<string, { url: string, priority: number, epoch: number }>} */
    this.queueByUrl = new Map();
    /** @type {Map<string, { url: string, priority: number, epoch: number, controller: AbortController }>} */
    this.inFlight = new Map();
    this.epoch = 0;
    this.activeWorkers = 0;
  }

  isUrlLoaded(url) {
    return globalLoadedUrls.has(url) || this.loadedUrls.has(url);
  }

  markUrlLoaded(url) {
    globalLoadedUrls.add(url);
    this.loadedUrls.add(url);
  }

  /**
   * Drop queued hotspot/thumb jobs and abort in-flight ones so preview/pano
   * can use the connection budget.
   */
  cancelLowPriorityWork() {
    this.queue = this.queue.filter((job) => {
      if (job.priority > PRIORITY.PANO) {
        this.queueByUrl.delete(job.url);
        return false;
      }
      return true;
    });
    for (const job of this.inFlight.values()) {
      if (job.priority > PRIORITY.PANO) {
        job.controller.abort();
      }
    }
  }

  stop() {
    const wasStopped = this.isStopped;
    this.isStopped = true;
    this.queue = [];
    this.queueByUrl.clear();
    for (const job of this.inFlight.values()) {
      job.controller.abort();
    }
    this.inFlight.clear();
    this.activeWorkers = 0;
    if (!wasStopped) {
      console.log("[Preloader] stop() — preload cancelled");
    }
  }

  clampSceneIndex(sceneIndex) {
    if (!this.scenes?.length) return 0;
    return Math.max(0, Math.min(sceneIndex, this.scenes.length - 1));
  }

  /**
   * Enqueue URLs at a priority. Dedupes by URL and keeps the better (lower) priority.
   */
  enqueue(urls, priority, { epoch = this.epoch } = {}) {
    if (this.isStopped) return;
    for (const url of urls) {
      if (!url || this.isUrlLoaded(url)) continue;
      if (this.inFlight.has(url)) {
        const flying = this.inFlight.get(url);
        // Already downloading; if a higher-priority request arrives, leave it running.
        if (flying && priority < flying.priority) {
          flying.priority = priority;
          flying.epoch = epoch;
        }
        continue;
      }

      const existing = this.queueByUrl.get(url);
      if (existing) {
        if (priority < existing.priority) {
          existing.priority = priority;
          existing.epoch = epoch;
        } else {
          existing.epoch = Math.max(existing.epoch, epoch);
        }
        continue;
      }

      const job = { url, priority, epoch };
      this.queue.push(job);
      this.queueByUrl.set(url, job);
    }
    this.pump();
  }

  pickNextJob() {
    if (!this.queue.length) return null;
    let bestIndex = 0;
    for (let i = 1; i < this.queue.length; i++) {
      const a = this.queue[i];
      const b = this.queue[bestIndex];
      if (
        a.priority < b.priority ||
        (a.priority === b.priority && a.epoch > b.epoch)
      ) {
        bestIndex = i;
      }
    }
    const [job] = this.queue.splice(bestIndex, 1);
    this.queueByUrl.delete(job.url);
    return job;
  }

  pump() {
    if (this.isStopped) return;
    while (
      this.activeWorkers < PRELOAD_CONCURRENCY &&
      this.queue.length > 0
    ) {
      const job = this.pickNextJob();
      if (!job) break;
      if (this.isUrlLoaded(job.url)) continue;
      this.startJob(job);
    }
  }

  startJob(job) {
    const controller = new AbortController();
    const tracked = { ...job, controller };
    this.inFlight.set(job.url, tracked);
    this.activeWorkers += 1;

    void (async () => {
      try {
        const response = await fetch(job.url, {
          signal: controller.signal,
          credentials: "same-origin",
        });
        if (!response.ok) {
          throw new Error(`Preload failed (${response.status}): ${job.url}`);
        }
        await response.blob();
        if (!this.isStopped && !controller.signal.aborted) {
          this.markUrlLoaded(job.url);
        }
      } catch (error) {
        if (!isAbortError(error) && !this.isStopped) {
          console.error("Error preloading image", job.url, error);
        }
      } finally {
        if (this.inFlight.get(job.url) === tracked) {
          this.inFlight.delete(job.url);
        }
        this.activeWorkers = Math.max(0, this.activeWorkers - 1);
        this.pump();
      }
    })();
  }

  /**
   * Drop queued preview/pano jobs and abort in-flight warm downloads that are
   * outside the new needed URL set. Also cancels competing hotspot/thumb work.
   */
  refreshWarmWindow(neededWarmUrls) {
    this.cancelLowPriorityWork();
    this.epoch += 1;
    const epoch = this.epoch;

    this.queue = this.queue.filter((job) => {
      if (job.priority <= PRIORITY.PANO) {
        this.queueByUrl.delete(job.url);
        return false;
      }
      job.epoch = epoch;
      return true;
    });

    for (const job of this.inFlight.values()) {
      if (job.priority <= PRIORITY.PANO && !neededWarmUrls.has(job.url)) {
        job.controller.abort();
      }
    }

    return epoch;
  }

  /** Blocking sequential fetch for the loader path (bypasses the queue). */
  async fetchUrlDirect(url) {
    if (this.isStopped || !url) return;
    if (this.isUrlLoaded(url)) return;

    const response = await fetch(url, { credentials: "same-origin" });
    if (!response.ok) {
      throw new Error(`Preload failed (${response.status}): ${url}`);
    }
    await response.blob();
    if (!this.isStopped) {
      this.markUrlLoaded(url);
    }
  }

  async fetchUrlsDirect(urls, { sequential = true } = {}) {
    const pending = [...new Set(urls)].filter(
      (url) => url && !this.isUrlLoaded(url),
    );
    if (!pending.length) return;

    if (sequential) {
      for (const url of pending) {
        if (this.isStopped) return;
        try {
          await this.fetchUrlDirect(url);
        } catch (error) {
          console.error("Error preloading image", url, error);
        }
      }
      return;
    }

    let nextIndex = 0;
    const workers = Array.from(
      { length: Math.min(PRELOAD_CONCURRENCY, pending.length) },
      async () => {
        while (nextIndex < pending.length) {
          if (this.isStopped) return;
          const url = pending[nextIndex++];
          try {
            await this.fetchUrlDirect(url);
          } catch (error) {
            console.error("Error preloading image", url, error);
          }
        }
      },
    );
    await Promise.all(workers);
  }

  getScenePreviewUrl(scene) {
    return scene.previewUrl ? [scene.previewUrl] : [];
  }

  getSceneThumbUrl(scene) {
    return scene.thumbUrl ? [scene.thumbUrl] : [];
  }

  getSceneEssentialUrls(scene) {
    return [...this.getScenePreviewUrl(scene), ...this.getSceneThumbUrl(scene)];
  }

  getScenePanoUrls(scene) {
    const urls = [];
    scene.images?.forEach((image) => {
      this.directions.forEach((direction) => {
        urls.push(image.url.replace("%s", direction));
      });
    });
    return urls;
  }

  getSceneImageUrls(scene) {
    return [
      ...this.getSceneEssentialUrls(scene),
      ...this.getScenePanoUrls(scene),
    ];
  }

  /**
   * Neighbor scene indices around the current index, wrapping at both ends.
   */
  getAdjacentSceneIndices(distance = PREVIEW_RADIUS) {
    const n = this.scenes?.length ?? 0;
    if (n <= 1) return [];

    const i = getCurrentSceneIndex();
    const indices = [];
    const seen = new Set();

    for (let d = 1; d <= distance; d++) {
      const next = (i + d) % n;
      const prev = (i - d + n) % n;
      for (const idx of [next, prev]) {
        if (idx === i || seen.has(idx)) continue;
        seen.add(idx);
        indices.push(idx);
      }
    }

    return indices;
  }

  collectWarmUrls() {
    const previewUrls = [];
    const needed = new Set();

    const currentIndex = getCurrentSceneIndex();
    const currentScene = this.scenes[currentIndex];
    if (currentScene) {
      for (const url of this.getScenePreviewUrl(currentScene)) {
        needed.add(url);
      }
    }

    for (const idx of this.getAdjacentSceneIndices(PREVIEW_RADIUS)) {
      const scene = this.scenes[idx];
      if (!scene) continue;
      for (const url of this.getScenePreviewUrl(scene)) {
        previewUrls.push(url);
        needed.add(url);
      }
      this.preloadedScenePreviewIndices.add(idx);
    }

    return { previewUrls, needed };
  }

  /**
   * Background warm disabled — only loader preload remains.
   */
  async warmAroundCurrent() {}

  async preloadNextScene() {}

  async preloadPreviousScene() {}

  findSceneByName(sceneName) {
    if (!sceneName) return null;
    return this.scenes.find((scene) => scene.name === sceneName) ?? null;
  }

  getHotspotStyleIconUrl(hotspot) {
    const defaultPath = HOTSPOT_STYLE_ICON_DEFAULTS[hotspot.style];
    if (!defaultPath) return null;

    const tourKey = TOUR_HOTSPOT_ICON_KEYS[hotspot.style];
    const tourUrl = tourKey ? this.tour?.[tourKey]?.url : null;
    return resolveTourAssetUrl(tourUrl ?? defaultPath);
  }

  getCustomPresetIconUrls(hotspot) {
    if (
      hotspot.style !== "hotspot_info" &&
      hotspot.style !== "hotspot_info_custom" &&
      hotspot.style !== "hotspot_directions"
    ) {
      return [];
    }

    const presetId = hotspot.customPresetImage?.trim();
    if (!presetId) return [];

    const colonIndex = presetId.indexOf(":");
    if (colonIndex <= 0) return [];

    const prefix = presetId.slice(0, colonIndex);
    const iconName = presetId.slice(colonIndex + 1);
    if (!prefix || !iconName) return [];

    const fillHex =
      hotspot.style === "hotspot_info_custom" ||
      hotspot.style === "hotspot_directions"
        ? hotspot.fillColor?.trim() || "#ffffff"
        : this.tour?.theme?.hotspotColor?.trim() ||
          this.tour?.theme?.primaryColor ||
          "#000000";
    const hex = fillHex.replace("#", "");
    const urls = [
      `https://api.iconify.design/${encodeURIComponent(prefix)}/${encodeURIComponent(iconName)}.svg?color=%23${encodeURIComponent(hex)}`,
    ];

    if (
      (hotspot.style === "hotspot_info_custom" ||
        hotspot.style === "hotspot_directions") &&
      hotspot.hoverFillColor?.trim()
    ) {
      const hoverHex = hotspot.hoverFillColor.replace("#", "").trim();
      if (hoverHex) {
        urls.push(
          `https://api.iconify.design/${encodeURIComponent(prefix)}/${encodeURIComponent(iconName)}.svg?color=%23${encodeURIComponent(hoverHex)}`,
        );
      }
    }

    return urls;
  }

  getHotspotAssetUrls(hotspot) {
    const urls = [];

    if (hotspot.popupImage?.url) {
      urls.push(resolveTourAssetUrl(hotspot.popupImage.url));
    }

    hotspot.galleryImages?.forEach((image) => {
      if (image?.url) urls.push(resolveTourAssetUrl(image.url));
    });

    if (hotspot.popupSocialContent?.thumbnailAsset?.url) {
      urls.push(
        resolveTourAssetUrl(hotspot.popupSocialContent.thumbnailAsset.url),
      );
    }

    if (
      hotspot.linkedScene &&
      LINKED_SCENE_HOTSPOT_STYLES.has(hotspot.style)
    ) {
      const linkedScene = this.findSceneByName(hotspot.linkedScene);
      if (linkedScene?.thumbUrl) {
        urls.push(resolveTourAssetUrl(linkedScene.thumbUrl));
      }
    }

    const styleIconUrl = this.getHotspotStyleIconUrl(hotspot);
    if (styleIconUrl) urls.push(styleIconUrl);

    urls.push(...this.getCustomPresetIconUrls(hotspot));

    return urls.filter(Boolean);
  }

  getSceneHotspotAssetUrls(scene) {
    const urls = [];
    if (scene.logo?.url) {
      urls.push(resolveTourAssetUrl(scene.logo.url));
    }

    const hotspots = scene.hotspots?.filter((hotspot) => !hotspot.hidden) ?? [];
    for (const hotspot of hotspots) {
      urls.push(...this.getHotspotAssetUrls(hotspot));
    }

    return urls;
  }

  /** Drop queued hotspot jobs (replaced on scene change); leave warm/thumbs alone. */
  clearQueuedHotspots() {
    this.queue = this.queue.filter((job) => {
      if (job.priority === PRIORITY.HOTSPOT) {
        this.queueByUrl.delete(job.url);
        return false;
      }
      return true;
    });
  }

  async preloadCurrentScene() {}

  async preloadThumbnails() {}

  getHotspotImageUrls(hotspot) {
    return this.getHotspotAssetUrls(hotspot);
  }

  async preloadHotspot(_hotspot) {}

  async preloadHotspots(_hotspots) {}

  /**
   * Loader gate: preview, thumb, then pano faces (sequential).
   * Bypasses the priority queue so the overlay can await completion.
   */
  async preloadStartSceneForLoader(sceneIndex) {
    const index = this.clampSceneIndex(sceneIndex);
    const scene = this.scenes[index];
    const t0 = performance.now();
    beginFirstScenePreload();
    console.log("[Preloader] preloadStartSceneForLoader start", {
      sceneIndex: index,
    });

    try {
      if (!scene) return;
      await this.fetchUrlsDirect(this.getScenePreviewUrl(scene), {
        sequential: true,
      });
      if (this.isStopped) return;
      this.preloadedScenePreviewIndices.add(index);

      await this.fetchUrlsDirect(this.getSceneThumbUrl(scene), {
        sequential: true,
      });
      if (this.isStopped) return;

      await this.fetchUrlsDirect(this.getScenePanoUrls(scene), {
        sequential: true,
      });
      if (this.isStopped) return;
      this.preloadedScenePanoIndices.add(index);

      console.log("[Preloader] preloadStartSceneForLoader done", {
        sceneIndex: index,
        elapsedMs: Math.round(performance.now() - t0),
      });
    } finally {
      completeFirstScenePreload();
    }
  }

  async preloadScenePreview(_sceneIndex) {}

  async preloadSceneThumb(_sceneIndex) {}

  /** Pano warm is loader-only; queue path is a no-op. */
  async preloadScenePano(_sceneIndex) {}

  async preloadSceneEssentials(sceneIndex) {
    const index = this.clampSceneIndex(sceneIndex);
    const scene = this.scenes[index];
    if (!scene) return;
    await this.fetchUrlsDirect(this.getSceneEssentialUrls(scene), {
      sequential: true,
    });
  }

  async preloadScene(_sceneIndex) {}

  async preloadFirstScene() {}

  async preloadStartScene(_sceneIndex) {}

  async initialPreload(_startSceneIndex = 0) {}
}
